'use client';

import React, { useCallback, useState, useTransition, useMemo } from 'react';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';
// eslint-disable-next-line import/no-unresolved
import { algoliasearch } from 'algoliasearch-next';
import {
  InstantSearch,
  Configure,
  // eslint-disable-next-line import/no-unresolved
} from 'react-instantsearch-next';
import getEnvValue from 'commons/getEnvValue';
import classNames from 'classnames';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import AlgoliaSearchBox from './components/AlgoliaSearchBox';
import AlgoliaHits from './components/AlgoliaHits';
import AdvancedSearchContext from './hooks/AdvancedSearchContext';

import useAdvancedSearchNextURL from './hooks/useAdvancedSearchNextURL';
import getHistoryRouter from './getHistoryRouter';
import MobileSearchIndexSelector from './components/MobileSearchIndexSelector';

const appId = getEnvValue('NEXT_PUBLIC_ALGOLIA_APPID');
const apiKey = getEnvValue('NEXT_PUBLIC_ALGOLIA_APIKEY');

const searchClient = algoliasearch(appId, apiKey);

const AdvancedSearchNext = () => {
  const {
    queryUrlParam,
    categoryUrlParam,
    updateUrlParam,
  } = useAdvancedSearchNextURL();

  const [currentProductIndex, setCurrentProductIndex] = useState(
    ALGOLIA_INDEX_NAME.PRODUCTS
  );
  const [currentSearchSection, setCurrentSearchSection] = useState(
    categoryUrlParam ?? ALGOLIA_INDEX_NAME.PRODUCTS
  );
  const [isReadyToShipActive, setIsReadyToShipActive] = useState(false);
  const [minWidth, setMinWidth] = useState('');
  const [maxWidth, setMaxWidth] = useState('');

  const [, startTransition] = useTransition();

  const updateSearchSection = useCallback(
    indexName => {
      startTransition(() => {
        setCurrentSearchSection(indexName);
        updateUrlParam({ category: indexName });
      });
    },
    [setCurrentSearchSection, updateUrlParam]
  );

  const updateCurrentProductIndex = useCallback(
    indexName => {
      startTransition(() => {
        setCurrentProductIndex(indexName);
      });
    },
    [setCurrentProductIndex]
  );

  const handleToggleReadyToShip = useCallback(checked => {
    setIsReadyToShipActive(checked);
  }, []);

  const handleWidthChange = useCallback((min, max) => {
    setMinWidth(min);
    setMaxWidth(max);
  }, []);

  const context = useMemo(
    () => ({
      currentSearchSection,
      currentProductIndex,
      updateSearchSection,
      updateCurrentProductIndex,
      isReadyToShipActive,
      handleToggleReadyToShip,
      minWidth,
      maxWidth,
      handleWidthChange,
    }),
    [
      currentSearchSection,
      currentProductIndex,
      updateSearchSection,
      updateCurrentProductIndex,
      isReadyToShipActive,
      handleToggleReadyToShip,
      minWidth,
      maxWidth,
      handleWidthChange,
    ]
  );

  // Configure routing for proper URL initialization
  // Try to get history router - works in new app, falls back to no routing in old app
  const routing = useMemo(() => {
    const historyRouter = getHistoryRouter();

    // If router is not available (old app), disable routing
    if (!historyRouter) {
      return undefined;
    }

    return {
      router: historyRouter({
        cleanUrlOnDispose: false,
        writeDelay: 1000, // Increased delay to prevent premature URL updates
      }),
      stateMapping: {
        stateToRoute(uiState) {
          const indexUiState = uiState[ALGOLIA_INDEX_NAME.PRODUCTS] || {};
          // Only update URL if query actually changes, not on refinement-only changes
          const query = indexUiState.query ?? queryUrlParam ?? '';
          return {
            q: query || undefined,
            category: currentSearchSection,
          };
        },
        routeToState(routeState) {
          return {
            [ALGOLIA_INDEX_NAME.PRODUCTS]: {
              // Preserve existing query if route doesn't have one
              query: routeState.q ?? queryUrlParam ?? '',
            },
          };
        },
      },
    };
  }, [currentSearchSection, queryUrlParam]);

  // Initialize InstantSearch with query from URL - using initialUiState as fallback
  // Note: when using routing, initialUiState should match what routing reads from URL
  const initialUiState = useMemo(
    () => ({
      [ALGOLIA_INDEX_NAME.PRODUCTS]: {
        query: queryUrlParam || '',
      },
    }),
    [queryUrlParam]
  );

  const numericFilters = useMemo(() => {
    const filters = ['item_availability<2', 'visibility>0', 'visibility<4'];

    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);

    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`width>=${minNum}`);
    }

    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`width<=${maxNum}`);
    }

    return filters;
  }, [minWidth, maxWidth]);

  return (
    <AdvancedSearchContext.Provider value={context}>
      <div
        className={classNames(
          'w-full mt-0 lg:mt-',
          '[&_.ais-SearchBox-input]:pl-[30px] [&_.ais-SearchBox-input]:indent:[unset] [&_.ais-SearchBox-input]:text-left',
          '[&_.ais-SearchBox-input::placeholder]:text-left [&_.ais-SearchBox-input::placeholder]:indent:[unset]'
        )}
      >
        {/* search box, then filters and results inside a plp shell component */}
        <InstantSearch
          searchClient={searchClient}
          indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
          {...(routing ? { routing } : {})}
          initialUiState={initialUiState}
        >
          {/* Add widgets here */}
          <div className="relative h-auto lg:!z-[1002]">
            <AlgoliaSearchBox />
          </div>

          <div className="mt-0 mx-0 mb-8 lg:mb-8 lg:flex">
            <MobileAndTabletOnly>
              <MobileSearchIndexSelector />
            </MobileAndTabletOnly>
          </div>

          <Configure
            facets={['*']}
            facetFilters={isReadyToShipActive ? ['is_in_stock:true'] : []}
            numericFilters={numericFilters}
            attributesToRetrieve={[
              '_highlightResult',
              'product_id',
              'name',
              'is_in_stock',
            ]}
            clickAnalytics
          />

          <AlgoliaHits />
        </InstantSearch>
      </div>
    </AdvancedSearchContext.Provider>
  );
};

export default AdvancedSearchNext;
